import { useEffect, useMemo, useState } from 'react';
import { previewPrompt, messageFrom } from './api';
import type { PromptDefinition, PromptLanguage, PromptPreview, PromptValidationIssue, PromptVersion } from './types';

interface Props {
  definition: PromptDefinition;
  /** The version being edited, or null when starting a new draft. */
  version: PromptVersion | null;
  /** Text the new draft starts from when `version` is null. */
  seed: { en: string; es: string };
  saving: boolean;
  onCancel: () => void;
  onSave: (body: { en: string; es: string; label: string; note?: string }) => Promise<void>;
  externalIssues: PromptValidationIssue[];
  externalError: string | null;
}

/**
 * The draft editor.
 *
 * Both languages are always in the payload, even when only one was touched.
 * Sending a partial save would be the easy way to blank the Spanish prompt of
 * a prompt whose English an operator was tuning, and that failure is silent —
 * Spanish-speaking patients get the compiled seed while the studio shows a
 * happily published version.
 *
 * The preview pane is the point of the screen. Validation catches a prompt that
 * is malformed; it cannot catch one that is well formed and wrong, and the only
 * thing that does is reading the prompt as the model will receive it, with the
 * placeholders filled.
 */
export function VersionEditor({
  definition,
  version,
  seed,
  saving,
  onCancel,
  onSave,
  externalIssues,
  externalError,
}: Props) {
  const [en, setEn] = useState(version?.en ?? seed.en);
  const [es, setEs] = useState(version?.es ?? seed.es);
  const [label, setLabel] = useState(version?.label ?? '');
  const [note, setNote] = useState(version?.note ?? '');
  const [language, setLanguage] = useState<PromptLanguage>('en');

  const [preview, setPreview] = useState<PromptPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    setEn(version?.en ?? seed.en);
    setEs(version?.es ?? seed.es);
    setLabel(version?.label ?? '');
    setNote(version?.note ?? '');
    setPreview(null);
  }, [version, seed.en, seed.es]);

  const text = language === 'en' ? en : es;

  /**
   * Placeholders the operator has dropped, shown before they hit save.
   * The backend refuses these too, but finding out while the text is still on
   * screen is the difference between a typo and a hunt.
   */
  const missingVariables = useMemo(
    () => definition.variables.filter((name) => !text.includes(`{{${name}}}`)),
    [definition.variables, text],
  );

  const missingTokens = useMemo(
    () => (definition.requiredTokens ?? []).filter((token) => !text.includes(token)),
    [definition.requiredTokens, text],
  );

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(await previewPrompt(definition.key, { en, es, language }));
    } catch (err) {
      setPreviewError(messageFrom(err));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="form-card full-width" data-testid="prompt-version-editor">
      <div className="ps-card-head">
        {/* The card heading takes the theme's uppercase tracked treatment, so the
            operator's own version label goes on its own line rather than being
            shouted back at them in 10.5px caps. */}
        <div style={{ minWidth: 0 }}>
          <h2>{version ? 'Editing draft' : 'New draft'}</h2>
          {version && <p className="ps-card-note">{version.label}</p>}
        </div>

        <div className="ps-seg">
          {(['en', 'es'] as const).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setLanguage(code)}
              aria-pressed={language === code}
              data-testid={`prompt-lang-${code}`}
              className={language === code ? 'active' : undefined}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="ps-field-grid">
        <label className="ps-field">
          Label
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. shorter triage questions"
            data-testid="prompt-version-label"
          />
        </label>
        <label className="ps-field">
          Note (optional)
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What this revision is trying to change"
            data-testid="prompt-version-note"
          />
        </label>
      </div>

      <label className="ps-field">
        Prompt text · {language.toUpperCase()}
        <textarea
          value={text}
          onChange={(e) => (language === 'en' ? setEn(e.target.value) : setEs(e.target.value))}
          rows={18}
          spellCheck={false}
          data-testid="prompt-version-text"
          className="ps-code"
        />
      </label>

      {(missingVariables.length > 0 || missingTokens.length > 0) && (
        <div className="ps-warning" data-testid="prompt-editor-warnings">
          {missingVariables.length > 0 && (
            <p>
              Missing placeholder{missingVariables.length > 1 ? 's' : ''} in{' '}
              {language.toUpperCase()}: {missingVariables.map((v) => `{{${v}}}`).join(', ')} — the
              context they carry would be dropped from the prompt.
            </p>
          )}
          {missingTokens.length > 0 && (
            <p>
              Missing required token{missingTokens.length > 1 ? 's' : ''}:{' '}
              {missingTokens.join(', ')} — the backend parses these out of the reply, and the
              feature stops working silently without them.
            </p>
          )}
        </div>
      )}

      {externalError && (
        <div className="error-banner" style={{ marginBottom: 0 }} data-testid="prompt-editor-error">
          {externalError}
          {externalIssues.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
              {externalIssues.map((issue, i) => (
                <li key={i}>
                  [{issue.language.toUpperCase()}] {issue.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="ps-actions">
        <button
          type="button"
          onClick={() => onSave({ en, es, label, note: note || undefined })}
          disabled={saving}
          data-testid="prompt-version-save"
          className="btn btn-primary"
        >
          {saving ? 'Saving…' : version ? 'Save draft' : 'Create draft'}
        </button>
        <button
          type="button"
          onClick={runPreview}
          disabled={previewing}
          data-testid="prompt-version-preview"
          className="btn btn-secondary"
        >
          {previewing ? 'Rendering…' : 'Preview rendered prompt'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary ps-actions-spacer"
        >
          Cancel
        </button>
      </div>

      {previewError && (
        <div className="error-banner" style={{ marginBottom: 0 }} data-testid="prompt-preview-error">
          {previewError}
        </div>
      )}

      {preview && (
        <div data-testid="prompt-preview">
          {preview.issues.length > 0 ? (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              <strong>This text would be rejected:</strong>
              <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
                {preview.issues.map((issue, i) => (
                  <li key={i}>
                    [{issue.language.toUpperCase()}] {issue.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="ps-card-note" style={{ marginBottom: '0.4rem' }}>
                Rendered as the model receives it.
                {preview.filledWithPlaceholder.length > 0 && (
                  <>
                    {' '}
                    Values shown as «name» are placeholders filled at runtime:{' '}
                    {preview.filledWithPlaceholder.join(', ')}.
                  </>
                )}
              </p>
              <pre className="ps-pre">{preview.rendered}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
